import type {SteamPlayer, BanInfo, Friend, SteamID64, VanityName, OwnedGame, Minutes, FriendData} from '../types/steam';
import {SteamID64 as toSteamID64, Minutes as toMinutes, BanCount as toBanCount, Days as toDays} from '../types/steam';
import {getFriends as getCachedFriends, setFriend as setCachedFriend, getFriendsList as getCachedFriendsList, setFriendsList as setCachedFriendsList} from './cache';

const API_BASE = '/steamapi';
const BATCH_SIZE = 50;

const CS2_APP_ID = 730;

interface PlayerResponse {
    response: {
        players: SteamPlayer[];
    };
}

interface BansResponse {
    players: BanInfo[];
}

interface FriendsResponse {
    friendslist: {
        friends: Friend[];
    };
}

interface OwnedGamesResponse {
    response: {
        games?: OwnedGame[];
    };
}

interface AliasesResponse {
    response: {
        aliases?: string[];
    };
}

export async function resolveVanityUrl(vanityName: VanityName): Promise<SteamID64> {
    try {
        const res: Response = await fetch(`${API_BASE}/ISteamUser/ResolveVanityURL/v0001/?vanityurl=${encodeURIComponent(
            vanityName)}`);

        // Check for backend connectivity issues
        if (!res.ok &&
            (
                res.status === 502 || res.status === 503 || res.status === 504
            )) {
            throw new Error('Unable to connect to backend server. The server may be down or unavailable.');
        }

        if (!res.ok) throw new Error(`Failed to resolve vanity name: ${res.statusText}`);

        const data: { response: { success: number; steamid: string } } = await res.json();
        if (data.response?.success !== 1) {
            throw new Error('Vanity name not found');
        }
        return toSteamID64(data.response.steamid);
    } catch (err: unknown) {
        if (err instanceof TypeError && err.message.includes('fetch')) {
            throw new Error('Unable to connect to backend server. Please check if the server is running.');
        }
        throw err;
    }
}

export async function getPlayerSummary(steamId64: SteamID64): Promise<SteamPlayer> {
    try {
        const res: Response = await fetch(`${API_BASE}/ISteamUser/GetPlayerSummaries/v0002/?steamids=${steamId64}`);

        // Check for backend connectivity issues
        if (!res.ok &&
            (
                res.status === 502 || res.status === 503 || res.status === 504
            )) {
            throw new Error('Unable to connect to backend server. The server may be down or unavailable.');
        }

        if (!res.ok) throw new Error(`Failed to fetch player summary: ${res.statusText}`);

        const data: PlayerResponse = await res.json();
        if (!data.response?.players?.length) {
            throw new Error('Player not found');
        }
        return data.response.players[0];
    } catch (err: unknown) {
        if (err instanceof TypeError && err.message.includes('fetch')) {
            throw new Error('Unable to connect to backend server. Please check if the server is running.');
        }
        throw err;
    }
}

export async function getRecentNames(steamId64: SteamID64): Promise<string[]> {
    try {
        const res: Response = await fetch(`${API_BASE}/ISteamUser/GetPlayerAliases/v1/?steamid=${steamId64}`);

        if (!res.ok) {
            console.warn(`Failed to fetch recent names: ${res.status} ${res.statusText}`);
            return [];
        }

        const data: AliasesResponse = await res.json();
        return data.response?.aliases || [];
    } catch (err: unknown) {
        console.warn('Error fetching recent names:', err);
        return [];
    }
}

export async function getPlayerBans(steamId64: SteamID64): Promise<BanInfo> {
    try {
        const res: Response = await fetch(`${API_BASE}/ISteamUser/GetPlayerBans/v1/?steamids=${steamId64}`);

        // Check for backend connectivity issues
        if (!res.ok &&
            (
                res.status === 502 || res.status === 503 || res.status === 504
            )) {
            throw new Error('Unable to connect to backend server. The server may be down or unavailable.');
        }

        if (!res.ok) throw new Error(`Failed to fetch player bans: ${res.statusText}`);

        const data: BansResponse = await res.json();
        if (!data.players?.length) {
            throw new Error('Ban info not found');
        }
        return data.players[0];
    } catch (err: unknown) {
        if (err instanceof TypeError && err.message.includes('fetch')) {
            throw new Error('Unable to connect to backend server. Please check if the server is running.');
        }
        throw err;
    }
}

export async function getFriendsList(steamId64: SteamID64): Promise<Friend[]> {
    // Check cache first
    const cached = getCachedFriendsList(steamId64);
    if (cached !== null) {
        return cached;
    }

    // Fetch from API if not in cache
    try {
        const res: Response = await fetch(`${API_BASE}/ISteamUser/GetFriendList/v0001/?steamid=${steamId64}&relationship=friend`);

        // Check for backend connectivity issues
        if (!res.ok &&
            (
                res.status === 502 || res.status === 503 || res.status === 504
            )) {
            throw new Error('Unable to connect to backend server. The server may be down or unavailable.');
        }

        if (!res.ok) {
            if (res.status === 401) {
                // Cache empty friends list for private profiles to avoid repeated 401s
                const emptyList: Friend[] = [];
                setCachedFriendsList(steamId64, emptyList);
                throw new Error('Friends list is private');
            }
            throw new Error(`Failed to fetch friends list: ${res.statusText}`);
        }

        const data: FriendsResponse = await res.json();
        const friendsList = data.friendslist?.friends || [];
        
        // Cache the result
        setCachedFriendsList(steamId64, friendsList);
        
        return friendsList;
    } catch (err: unknown) {
        if (err instanceof TypeError && err.message.includes('fetch')) {
            throw new Error('Unable to connect to backend server. Please check if the server is running.');
        }
        throw err;
    }
}

export async function getPlayersBatch(steamIds: SteamID64[]): Promise<SteamPlayer[]> {
    if (steamIds.length === 0) return [];

    const chunks: SteamID64[][] = [];
    for (let i = 0; i < steamIds.length; i += BATCH_SIZE) {
        chunks.push(steamIds.slice(i, i + BATCH_SIZE));
    }

    try {
        const results: SteamPlayer[][] = await Promise.all(chunks.map(async (chunk: SteamID64[]): Promise<SteamPlayer[]> => {
            const ids: string = chunk.join(',');
            const res: Response = await fetch(`${API_BASE}/ISteamUser/GetPlayerSummaries/v0002/?steamids=${ids}`);
            if (!res.ok) {
                console.error(`Failed to fetch player summaries batch: ${res.status} ${res.statusText}`);
                return [];
            }
            const data: PlayerResponse = await res.json();
            return data.response?.players || [];
        }));

        return results.flat();
    } catch (err: unknown) {
        if (err instanceof TypeError && err.message.includes('fetch')) {
            throw new Error('Unable to connect to backend server. Please check if the server is running.');
        }
        throw err;
    }
}

export async function getBansBatch(steamIds: SteamID64[]): Promise<BanInfo[]> {
    if (steamIds.length === 0) return [];

    const chunks: SteamID64[][] = [];
    for (let i = 0; i < steamIds.length; i += BATCH_SIZE) {
        chunks.push(steamIds.slice(i, i + BATCH_SIZE));
    }

    try {
        const results: BanInfo[][] = await Promise.all(chunks.map(async (chunk: SteamID64[]): Promise<BanInfo[]> => {
            const ids: string = chunk.join(',');
            const res: Response = await fetch(`${API_BASE}/ISteamUser/GetPlayerBans/v1/?steamids=${ids}`);
            if (!res.ok) {
                console.error(`Failed to fetch player bans batch: ${res.status} ${res.statusText}`);
                return [];
            }
            const data: BansResponse = await res.json();
            return data.players || [];
        }));

        return results.flat();
    } catch (err: unknown) {
        if (err instanceof TypeError && err.message.includes('fetch')) {
            throw new Error('Unable to connect to backend server. Please check if the server is running.');
        }
        throw err;
    }
}

export async function getCS2Playtime(steamId64: SteamID64): Promise<Minutes | undefined> {
    try {
        // Build URL with properly encoded parameters
        const url = `${API_BASE}/IPlayerService/GetOwnedGames/v1/?steamid=${steamId64}&appids_filter%5B0%5D=${CS2_APP_ID}&include_played_free_games=1`;
        const res: Response = await fetch(url);

        // Check for backend connectivity issues
        if (!res.ok &&
            (
                res.status === 502 || res.status === 503 || res.status === 504
            )) {
            throw new Error('Unable to connect to backend server. The server may be down or unavailable.');
        }

        if (!res.ok) {
            // If we get an error (e.g., private profile), return undefined instead of throwing
            console.warn(`Failed to fetch CS2 playtime: ${res.status} ${res.statusText}`);
            return undefined;
        }

        const data: OwnedGamesResponse = await res.json();
        const cs2Game = data.response?.games?.find((game: OwnedGame) => game.appid === CS2_APP_ID);
        return cs2Game ? toMinutes(cs2Game.playtime_forever) : undefined;
    } catch (err: unknown) {
        if (err instanceof TypeError && err.message.includes('fetch')) {
            throw new Error('Unable to connect to backend server. Please check if the server is running.');
        }
        // For any other error, log and return undefined (non-fatal)
        console.warn('Error fetching CS2 playtime:', err);
        return undefined;
    }
}

export async function getFriendsOfFriends(
    steamIds: SteamID64[],
    limit?: number,
    fofLimit?: number,
): Promise<Map<SteamID64, FriendData[]>> {
    const result = new Map<SteamID64, FriendData[]>();

    // Limit the number of friends we fetch friends for
    const limitedIds = (
        limit && limit < steamIds.length
    ) ? steamIds.sort().slice(0, limit) : steamIds;

    const uniqueIds = new Set<SteamID64>();
    for (const steamId of limitedIds) {
        try {
            // Get friends list
            const friends = await getFriendsList(steamId);
            if (friends.length === 0) continue;

            // Limit friends of friends to prevent too much data
            const newIds = friends.map(f => f.steamid)
                .filter(f => !uniqueIds.has(f))

            const friendIds = (fofLimit && fofLimit < newIds.length)
                ? newIds.sort().slice(0, fofLimit)
                : newIds;

            newIds.forEach(id => uniqueIds.add(id));

            // Use cache to get friends data, same as NetworkPage does for direct friends
            const {cached, missing} = getCachedFriends(friendIds);
            const friendData: FriendData[] = Object.values(cached);

            // Only fetch missing data
            if (missing.length > 0) {
                const [players, bans] = await Promise.all([
                    getPlayersBatch(missing), getBansBatch(missing),
                ]);

                // Combine into FriendData and cache it
                players.forEach(player => {
                    const banInfo = bans.find(b => b.SteamId === player.steamid);
                    const data: FriendData = {
                        player, bans: banInfo || {
                            SteamId: player.steamid,
                            CommunityBanned: false,
                            VACBanned: false,
                            NumberOfVACBans: toBanCount(0),
                            DaysSinceLastBan: toDays(0),
                            NumberOfGameBans: toBanCount(0),
                            EconomyBan: 'none',
                        },
                    };
                    friendData.push(data);
                    setCachedFriend(player.steamid, data);
                });
            }

            result.set(steamId, friendData);
        } catch (err) {
            // Skip friends with private profiles or errors
            console.warn(`Could not fetch friends for ${steamId}:`, err);
        }
    }

    console.log("Friends of friends fetched: ", result.size);

    return result;
}
